import { ref } from 'vue'
import { routing } from '@/Support/Routing/Functions/routing'
import { t } from '@/Support/lang'

/* =========================
 * Types & Enums
 * ========================= */

export enum MessageRole {
  User = 'user',
  Assistant = 'assistant',
  Error = 'error',
  Link = 'link',
}

export interface ConversationMessage {
  role: MessageRole
  content: string
  conversationId?: string | null
  id?: string | null
  status?: string
  link?: string
  visit?: boolean
}

interface StartOptions {
  prompt: string
  model?: string
  conversationId?: string | null
  extraParams?: Record<string, string | number | boolean | null | undefined>
  onMessageReceived?: (message?: ConversationMessage) => void
  onFinish?: () => void
  onLink?: (url: string) => void
}

/* =========================
 * Composable
 * ========================= */

export function useAiStream() {
  const messages = ref<ConversationMessage[]>([])
  const loading = ref(false)
  const chatting = ref(false)
  const error = ref<string | null>(null)
  const conversationId = ref<string | null>(null)

  let abortController: AbortController | null = null

  /* =========================
   * Helpers
   * ========================= */

  function stop() {
    chatting.value = false
    if (abortController) {
      abortController.abort()
      abortController = null
    }
  }

  function appendUser(text: string) {
    messages.value.push({ role: MessageRole.User, content: text })
  }

  function appendAssistant(
    message: ConversationMessage = {
      role: MessageRole.Assistant,
      content: '',
    },
  ) {
    const index = getLastEmptyAssistantIndex()
    if (index === -1) {
      messages.value.push(message)
    } else {
      messages.value[index] = message
    }
  }

  function appendAssistantDelta(message: ConversationMessage, replace = false) {
    const index = getMessageIndexById(message)
    if (index === -1) {
      appendAssistant(message)
      return
    }

    replace
      ? (messages.value[index].content = message.content)
      : (messages.value[index].content += message.content)
  }

  function setError(message: string) {
    error.value = message
    appendAssistant({ role: MessageRole.Error, content: message })
  }

  const getLastEmptyAssistantIndex = () =>
    messages.value.findLastIndex(
      m => m.role === MessageRole.Assistant && m.content === '',
    )

  const getMessageIndexById = (message: ConversationMessage) =>
    messages.value.findLastIndex(m => m.id && message.id && m.id === message.id)

  /* =========================
   * Main start()
   * ========================= */

  async function start(options: StartOptions) {
    error.value = null
    loading.value = true
    chatting.value = true

    options.onMessageReceived?.()

    abortController = new AbortController()

    try {
      const response = await fetch(routing('ai.responses.stream.post'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        signal: abortController.signal,
        body: JSON.stringify({
          prompt: options.prompt,
          model: options.model,
          ai_conversation: options.conversationId,
          ...options.extraParams,
        }),
      })

      /* ===== Authorization / credit errors ===== */
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error('No stream reader available')
      }

      const decoder = new TextDecoder()
      let buffer = ''

      loading.value = false

      while (true) {
        const { value, done } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        const chunks = buffer.split('\n\n')
        buffer = chunks.pop() || ''

        for (const chunk of chunks) {
          handleSseChunk(chunk, options)
        }
      }

      options.onFinish?.()
      stop()
    } catch (e: any) {
      if (e.name !== 'AbortError') {
        console.error(e)
        setError(t('ai.query.response.error'))
      }
      loading.value = false
      options.onFinish?.()
      stop()
    }
  }

  /* =========================
   * SSE parser
   * ========================= */

  function handleSseChunk(chunk: string, options: StartOptions) {
    let event = 'message'
    let data = ''

    for (const line of chunk.split('\n')) {
      if (line.startsWith('event:')) {
        event = line.replace('event:', '').trim()
      }
      if (line.startsWith('data:')) {
        data += line.replace('data:', '').trim()
      }
    }

    if (!data) return

    try {
      const payload = JSON.parse(data) as ConversationMessage

      switch (event) {
        case 'conversation':
          conversationId.value = (payload as any)?.id ?? null
          break

        case 'full message':
          appendAssistant(payload)
          appendAssistant()
          if (payload.link && payload.visit) {
            options.onLink?.(payload.link)
          }
          break

        case 'progress square':
          appendAssistantDelta(payload, true)
          if (payload.status === 'done') {
            appendAssistant()
          }
          break

        case 'done':
          options.onFinish?.()
          stop()
          break

        default:
          appendAssistantDelta(payload)
          options.onMessageReceived?.(payload)
      }
    } catch (e) {
      console.error('SSE parse error', e, data)
    }
  }

  /* =========================
   * Public API
   * ========================= */

  return {
    messages,
    loading,
    error,
    conversationId,
    chatting,
    start,
    stop,
    appendUser,
    appendAssistant,
    appendAssistantDelta,
    setError,
  }
}
